import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg, eur, STATUS } from "@/lib/api";
import { StatusBadge } from "@/pages/Account";

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/orders", { params: { status: filter === "all" ? undefined : filter } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); toast.success(`Pedido marcado como ${STATUS[status].toLowerCase()}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="w-56 mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="admin-orders-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los estados</SelectItem>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {orders.length === 0 ? <p className="text-slate-500" data-testid="admin-orders-empty">No hay pedidos.</p> : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="border border-slate-200 rounded p-4 grid md:grid-cols-[1fr_1fr_200px] gap-4" data-testid={`admin-order-${o.id}`}>
              <div>
                <p className="font-display font-bold text-lg text-[#0B1D3A]">{o.number} <StatusBadge s={o.status} />{o.test_payment && <span className="ml-2 text-[10px] font-bold uppercase bg-[#DC2626] text-white px-2 py-0.5 rounded-full" data-testid={`admin-order-test-${o.id}`}>Pago de prueba · no enviar</span>}{o.gift && <span className="ml-2 text-[10px] font-bold uppercase bg-[#2563EB] text-white px-2 py-0.5 rounded-full" data-testid={`admin-order-gift-${o.id}`}>Regalo · albarán sin precios</span>}</p>
                <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("es-ES")} · {o.payment_method === "bizum" ? "Bizum" : "Tarjeta"}</p>
                <ul className="text-sm mt-2">{o.items.map((i) => <li key={i.slug + i.format_id}>{i.name} · {i.format_label} × {i.qty}</li>)}</ul>
              </div>
              <div className="text-sm text-slate-600">
                <p className="font-semibold text-slate-800">{o.shipping_address.name}</p>
                <p>{o.shipping_address.address}, {o.shipping_address.postal_code} {o.shipping_address.city} ({o.shipping_address.province})</p>
                <p>{o.shipping_address.phone} · {o.user_email}</p>
              </div>
              <div className="space-y-2">
                <p className="font-bold text-[#DC2626] text-lg">{eur(o.total)}</p>
                <Select value={o.status} onValueChange={(v) => change(o.id, v)}>
                  <SelectTrigger data-testid={`admin-order-status-${o.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`admin-status-option-${k}`}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GuideRow = ({ g, slugs, onSaved }) => {
  const [v, setV] = useState({ title: g.title, reason: g.reason, products: g.products.join(", ") });
  const save = async () => {
    const products = v.products.split(",").map((s) => s.trim()).filter(Boolean);
    const bad = products.filter((s) => !slugs.includes(s));
    if (bad.length) return toast.error(`Productos desconocidos: ${bad.join(", ")}`);
    try { await api.put(`/admin/guide/${g.key}`, { title: v.title, reason: v.reason, products }); toast.success("Situación guardada"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border border-slate-200 rounded p-4 space-y-2" data-testid={`admin-guide-${g.key}`}>
      <Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid={`admin-guide-title-${g.key}`} />
      <Textarea rows={2} value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} />
      <div className="flex gap-2 items-center">
        <Input value={v.products} onChange={(e) => setV({ ...v, products: e.target.value })} placeholder="slugs separados por comas" data-testid={`admin-guide-products-${g.key}`} />
        <button onClick={save} data-testid={`admin-guide-save-${g.key}`} className="bg-[#DC2626] text-white text-sm font-bold px-4 py-2 rounded-full whitespace-nowrap">Guardar</button>
      </div>
    </div>
  );
};

export const AdminGuide = ({ slugs }) => {
  const [guide, setGuide] = useState([]);
  const load = () => api.get("/guide").then((r) => setGuide(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Productos disponibles: {slugs.join(", ")}</p>
      {guide.map((g) => <GuideRow key={g.key} g={g} slugs={slugs} onSaved={load} />)}
    </div>
  );
};

export const AdminCompany = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/legal").then((r) => setC(r.data)); }, []);
  if (!c) return null;
  const save = async () => {
    try {
      const { legal_name, cif, address, email, phone, registry, delivery_time } = c;
      await api.put("/admin/company", { legal_name, cif, address, email, phone, registry, delivery_time });
      toast.success("Datos de empresa guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const F = [["legal_name", "Razón social"], ["cif", "NIF / CIF"], ["address", "Domicilio fiscal"], ["email", "Email de contacto"], ["phone", "Teléfono"], ["registry", "Datos registrales (Registro Mercantil…)"], ["delivery_time", "Plazo de entrega"]];
  return (
    <div className="max-w-xl space-y-4" data-testid="admin-company">
      <p className="text-sm text-slate-500">Estos datos aparecen en el aviso legal, la política de privacidad, la de cookies y las condiciones de venta.</p>
      {F.map(([k, l]) => (
        <div key={k}><Label>{l}</Label><Input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`admin-company-${k}`} className="mt-1" /></div>
      ))}
      <button onClick={save} data-testid="admin-company-save" className="bg-[#DC2626] text-white font-display font-bold px-6 py-3 rounded-full">Guardar</button>
    </div>
  );
};

export const AdminSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/settings").then((r) => live && setS((cur) => cur || { shipping_cost: r.data.shipping_cost, free_shipping_from: r.data.free_shipping_from, discount_tiers: r.data.discount_tiers }));
    return () => { live = false; };
  }, []);
  if (!s) return null;
  const setTier = (i, k, val) => setS({ ...s, discount_tiers: s.discount_tiers.map((t, j) => (j === i ? { ...t, [k]: val } : t)) });
  const save = async () => {
    try {
      await api.put("/admin/settings", { shipping_cost: Number(s.shipping_cost), free_shipping_from: Number(s.free_shipping_from),
        discount_tiers: s.discount_tiers.filter((t) => Number(t.min) > 0 && Number(t.pct) > 0).map((t) => ({ min: Number(t.min), pct: Number(t.pct) })) });
      toast.success("Ajustes guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-md space-y-4" data-testid="admin-settings">
      <div><Label>Coste de envío (€)</Label><Input type="number" step="0.01" value={s.shipping_cost} onChange={(e) => setS({ ...s, shipping_cost: e.target.value })} data-testid="admin-shipping-cost-input" className="mt-1" /></div>
      <div><Label>Envío gratis desde (€)</Label><Input type="number" step="0.01" value={s.free_shipping_from} onChange={(e) => setS({ ...s, free_shipping_from: e.target.value })} data-testid="admin-free-shipping-input" className="mt-1" /></div>
      <div>
        <Label>Descuentos por volumen de compra</Label>
        <p className="text-xs text-slate-500">Se aplica el mayor descuento alcanzado según el importe de productos del pedido.</p>
        <div className="space-y-2 mt-2">
          {s.discount_tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`admin-tier-${i}`}>
              <span>Desde</span><Input type="number" value={t.min} onChange={(e) => setTier(i, "min", e.target.value)} className="w-24" data-testid={`admin-tier-min-${i}`} /><span>€ →</span>
              <Input type="number" value={t.pct} onChange={(e) => setTier(i, "pct", e.target.value)} className="w-20" data-testid={`admin-tier-pct-${i}`} /><span>%</span>
              <button onClick={() => setS({ ...s, discount_tiers: s.discount_tiers.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-[#DC2626] ml-1" data-testid={`admin-tier-remove-${i}`}>Quitar</button>
            </div>
          ))}
          <button onClick={() => setS({ ...s, discount_tiers: [...s.discount_tiers, { min: "", pct: "" }] })} className="text-sm font-bold text-[#2563EB]" data-testid="admin-tier-add">+ Añadir tramo</button>
        </div>
      </div>
      <button onClick={save} data-testid="admin-settings-save" className="bg-[#DC2626] text-white font-display font-bold px-6 py-3 rounded-full">Guardar</button>
    </div>
  );
};
